// The pre-made scope of works Stage 1 lifts onto the project
// (formData.preMadeScopeDoc), chosen by what a document says (CN4, Peter
// 2026-10-13: "file names shouldnt dictate anything. only the contents.").
//
// The lifted text is the AUTHORITATIVE brief for the strategy, the Work Method
// and the quote. Before CN4 a document became it by its file name ("Scope.pdf",
// "... SOW (MMYY).rtf"), by an AI label that sees the file name, or by a loose
// text rule every one of the business's own quotes met. Now the text alone
// decides (detectScopeOfWorksByContent). Kept outside Stage1DataEntry.tsx so it
// can be tested without mounting the intake page.

import { detectScopeOfWorksByContent, type ScopeOfWorksContentResult } from '../utils/scope-of-works-content-detector';

export interface Stage1PreMadeScopeDoc {
    sourceFilename: string;
    text: string;
    detectedAt: string;
}

function documentText(document: any): string {
    const value = document?.content ?? document?.text ?? '';
    return typeof value === 'string' ? value : '';
}

// The page sizes the importer kept for a PDF, first page first.
function documentPages(document: any): { width: unknown; height: unknown }[] | null {
    const pages = document?.pdfEvidence?.pages;
    if (!Array.isArray(pages) || pages.length === 0) return null;
    return pages.map((page: any) => ({ width: page?.width, height: page?.height }));
}

// Only extracted text is read: a placeholder the importer writes for a file it
// could not read ("[PDF contains 3 page(s) of image-based content ...]") is not.
function readableText(text: string): boolean {
    return text.length > 100 && !text.startsWith('[');
}

/** Whether the document's own text reads as a client's or consultant's scope of works. */
export function readStage1ScopeOfWorks(document: any): ScopeOfWorksContentResult {
    const text = documentText(document);
    return detectScopeOfWorksByContent({ text: readableText(text) ? text : '', pages: documentPages(document) });
}

/**
 * The document to lift as the pre-made scope of works: the first whose text
 * reads as one. Neither its file name nor the class a classifier gave it is
 * read; a request for a quote can be a scope too (a specification issued for
 * tender).
 */
export function selectStage1PreMadeScopeDocument(docs: any[]): any {
    const list = Array.isArray(docs) ? docs : [];
    return list.find(doc => doc && typeof doc === 'object' && readStage1ScopeOfWorks(doc).isScopeOfWorks) || null;
}

// Only a document with no other class is relabelled.
const UNCLASSIFIED_TYPES: ReadonlySet<string> = new Set(['', 'other', 'reference', 'scope_of_works']);

/**
 * Mark the lifted document as the scope of works. A document that already has
 * a class keeps it: the RFQ is decided by its own text (CN2), and a request
 * that is also a scope stays the request; a report stays a report (CN3).
 */
export function markStage1PreMadeScopeDocument(document: any): any {
    if (!document || typeof document !== 'object') return document;
    if (UNCLASSIFIED_TYPES.has(String(document.type ?? '').trim().toLowerCase())) document.type = 'scope_of_works';
    return document;
}

export function buildStage1PreMadeScopeDoc(document: any, now: Date = new Date()): Stage1PreMadeScopeDoc {
    return {
        sourceFilename: document?.name || 'scope.pdf',
        text: documentText(document),
        detectedAt: now.toISOString(),
    };
}
